import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Box } from '@mui/material';
import { sendPrivacyAssistantMessage, fetchPrivacyAssistantContext } from '../../services/privacyAssistantService';
import { authService } from '../../services/authService';
import { OPENING_MESSAGE, PAGE_PANEL_SX, buildWelcomeChipLabels, LOADING_HINTS } from './constants';
import ChatPanel from './ChatPanel';
import ChatLauncher from './ChatLauncher';

/**
 * @param {'floating' | 'page'} [variant='floating'] — page: full main-column assistant (no FAB).
 */
const PrivacyAssistantChat = ({ variant = 'floating' }) => {
  const isPage = variant === 'page';
  const [open, setOpen] = useState(isPage);
  const [messages, setMessages] = useState([OPENING_MESSAGE]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestionChips, setSuggestionChips] = useState([]);
  const [errorText, setErrorText] = useState('');
  const [showRetry, setShowRetry] = useState(false);
  const [contextHint, setContextHint] = useState(null);
  const [loadingHintIndex, setLoadingHintIndex] = useState(0);
  const messagesEndRef = useRef(null);
  const lastUserTextRef = useRef('');
  const loadingRef = useRef(false);

  const welcomeChipLabels = useMemo(() => buildWelcomeChipLabels(contextHint), [contextHint]);

  useEffect(() => {
    let cancelled = false;
    fetchPrivacyAssistantContext()
      .then((hint) => {
        if (!cancelled && hint) setContextHint(hint);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  useEffect(() => {
    if (!loading) return undefined;
    const id = setInterval(() => {
      setLoadingHintIndex((i) => (i + 1) % LOADING_HINTS.length);
    }, 2200);
    return () => clearInterval(id);
  }, [loading]);

  const hasUserMessage = messages.some((m) => m.role === 'user');
  const showEmptyChips = !hasUserMessage;

  const scrollActive = isPage || open;
  useEffect(() => {
    if (scrollActive && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, scrollActive, loading]);

  const runSend = useCallback(async (rawText, { appendUser = true } = {}) => {
    const text = String(rawText || '').trim();
    if (!text || loadingRef.current) return;

    lastUserTextRef.current = text;
    if (appendUser) {
      const userMessage = { id: Date.now(), role: 'user', text };
      setMessages((prev) => [...prev, userMessage]);
      setInputValue('');
    }

    loadingRef.current = true;
    setLoading(true);
    setLoadingHintIndex(0);
    setErrorText('');
    setShowRetry(false);
    setSuggestionChips([]);

    const stored = authService.getStoredUser();
    const platform = stored?.active_platform || stored?.platform || null;

    try {
      const data = await sendPrivacyAssistantMessage(text, platform);
      if (data.contextHint) {
        setContextHint(data.contextHint);
      }
      const structured = {
        explanation: data.explanation,
        why_it_matters: data.why_it_matters,
        urgency: data.urgency,
        steps: data.steps,
        showUrgencyChip: data.showUrgencyChip,
        showExplanation: data.showExplanation,
        showWhy: data.showWhy,
        showSteps: data.showSteps,
      };
      const useStructuredBody =
        structured.showUrgencyChip ||
        structured.showExplanation ||
        structured.showWhy ||
        structured.showSteps ||
        structured.steps?.length > 0 ||
        structured.urgency ||
        structured.explanation ||
        structured.why_it_matters;
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          role: 'bot',
          text: data.reply || 'No response text returned.',
          structured: useStructuredBody ? structured : undefined,
        },
      ]);
      setSuggestionChips(Array.isArray(data.suggestions) ? data.suggestions : []);
    } catch (err) {
      console.error('PrivacyAssistantChat send error:', err);
      setErrorText(err?.response?.data?.message || err.message || 'Network error');
      setShowRetry(true);
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          role: 'bot',
          text: 'Sorry — I could not reach the assistant. Check your connection and try again.',
        },
      ]);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, []);

  const handleSend = () => runSend(inputValue, { appendUser: true });

  const handleRetry = () => {
    const t = lastUserTextRef.current;
    if (!t || loadingRef.current) return;
    setShowRetry(false);
    setErrorText('');
    setMessages((prev) => (prev.length && prev[prev.length - 1]?.role === 'bot' ? prev.slice(0, -1) : prev));
    runSend(t, { appendUser: false });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleChipSelect = (label) => {
    runSend(label, { appendUser: true });
  };

  const loadingHint = loading ? LOADING_HINTS[loadingHintIndex % LOADING_HINTS.length] : '';

  const panel = (
    <ChatPanel
      open={isPage || open}
      onClose={() => setOpen(false)}
      showClose={!isPage}
      paperSx={isPage ? PAGE_PANEL_SX : undefined}
      messages={messages}
      loading={loading}
      loadingHint={loadingHint}
      messagesEndRef={messagesEndRef}
      showEmptyChips={showEmptyChips}
      welcomeChipLabels={welcomeChipLabels}
      suggestionChips={suggestionChips}
      onChipSelect={handleChipSelect}
      inputValue={inputValue}
      setInputValue={setInputValue}
      onSend={handleSend}
      onKeyDown={handleKeyDown}
      errorText={errorText}
      showRetry={showRetry}
      onRetry={handleRetry}
    />
  );

  if (isPage) {
    return (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          alignItems: 'stretch',
        }}
      >
        {panel}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 1300,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
      }}
    >
      {panel}
      <ChatLauncher open={open} onToggle={() => setOpen(!open)} />
    </Box>
  );
};

export default PrivacyAssistantChat;
